import React, { useMemo, useState } from "react";

import { Box, Grid, IconButton, Typography } from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import ShipeeziNewGrid from "Components/AppGrid/shipeezi-new-grid";
import { colors } from "@zapalacorp/tokens";
import { Input } from "Components/Form";
import { Color } from "Components/Form/Button";
import { SplitButton } from "Components/Form/SplitButton";
import IconCloseCircle from "Components/Generic/Icons/CloseCircle";
import { Member } from "Models/members/member";
import { useTranslation } from "react-i18next";
import { BsThreeDotsVertical } from "react-icons/bs";

import { ButtonGroup } from "../../ButtonGroup";
import AddMemberModal from "../AddMemberModal";
import useStyles, {
  AddMemberButtonContainer,
  MemberCardAvatar,
  MemberCardContainer,
  MemberCardContent,
  MemberCardInfo,
  MemberCardOptions,
  MemberCardWrapper,
  MemberHeaderContainer,
  MemberListAvatarBadge,
  MemberListAvatarContainer,
  MemberListContainer,
  MemberSearchFieldContainer,
  MoreOptionsMenu,
  OptionMenu,
} from "./styles";

interface MembersStepProps {
  membersAdded: Member[];
  setMembersAdded: React.Dispatch<React.SetStateAction<Member[]>>;
}

const noUserLogo = require("../../../../../../../../assets/img/noUserLogo.png");

enum ListOption {
  GALLERY = "GALLERY",
  LIST = "LIST",
}

const getFirstLetters = (fullname: string) => {
  const splitted = (fullname || "").split(" ");
  const first = splitted[0] ?? "";
  const last = splitted[splitted.length - 1] ?? "";

  return [first, last].map((value) => value.substring(0, 1).toUpperCase()).join("");
};

export const MembersStep = ({ membersAdded, setMembersAdded }: MembersStepProps) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();

  const [isAddMemberModalOpened, setIsAddMemberModalOpened] = useState(false);
  const [removeMemberAnchor, setRemoveMemberAnchor] = useState<null | HTMLElement>(null);
  const [memberListActive, setMemberListActive] = useState(ListOption.GALLERY);
  const [searchMemberValue, setSearchMemberValue] = useState("");
  const [memberIdToRemove, setMemberIdToRemove] = useState<number>();

  const onAddMembersSelected = (members: Member[]) => {
    const map: Map<number, Member> = new Map();

    members?.forEach((member) => {
      if (member.id) {
        map.set(member.id, member);
      }
    });

    setMembersAdded(Array.from(map.values()));
  };

  const onRemoveMemberSelected = (memberId: number) => {
    if (!memberId) return;

    setMembersAdded((prev) => prev.filter((member) => Number(member.id) !== Number(memberId)));
  };

  const membersRenderable = useMemo(() => {
    if (!searchMemberValue.trim()) {
      return membersAdded;
    }

    const inLower = (searchMemberValue || "")?.toLowerCase();

    return (membersAdded || []).filter((member) =>
      member?.fullName?.toLowerCase().includes(inLower)
    );
  }, [membersAdded, searchMemberValue]);

  const renderMemberContentByOption = () => {
    if (memberListActive === ListOption.GALLERY) {
      return (
        <MemberCardContainer item xs={12} container direction="row">
          {membersRenderable.map((member) => {
            return (
              <MemberCardWrapper key={member.id} item>
                <MemberCardContent>
                  <MemberCardAvatar>
                    <span>{getFirstLetters(member.fullName)}</span>
                  </MemberCardAvatar>
                  <MemberCardInfo>
                    <span>{member?.typeOfMember}</span>
                    <span>{member?.fullName}</span>
                    <span>{member?.email}</span>
                  </MemberCardInfo>
                  <MemberCardOptions>
                    <IconButton
                      onClick={(event) => {
                        setRemoveMemberAnchor(event.currentTarget);
                        setMemberIdToRemove(member.id);
                      }}
                    >
                      <BsThreeDotsVertical color={colors.gray600} size={16} />
                    </IconButton>
                  </MemberCardOptions>
                </MemberCardContent>
              </MemberCardWrapper>
            );
          })}
          <MoreOptionsMenu
            anchorEl={removeMemberAnchor}
            open={Boolean(removeMemberAnchor)}
            onClose={() => setRemoveMemberAnchor(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <OptionMenu
              onClick={() => {
                if (memberIdToRemove) onRemoveMemberSelected(memberIdToRemove);
                setMemberIdToRemove(undefined);
                setRemoveMemberAnchor(null);
              }}
            >
              {translate("remove")}
            </OptionMenu>
          </MoreOptionsMenu>
        </MemberCardContainer>
      );
    }
    if (memberListActive === ListOption.LIST) {
      return (
        <MemberListContainer>
          <ShipeeziNewGrid
            data={membersRenderable}
            totalItems={membersRenderable.length}
            pageSize={membersRenderable.length}
            columns={[
              {
                name: "fullName",
                title: translate("name"),
                formatComponent: (value) => {
                  return (
                    <MemberListAvatarContainer>
                      <MemberListAvatarBadge>
                        <span>{getFirstLetters(value)}</span>
                      </MemberListAvatarBadge>
                      <span>{value}</span>
                    </MemberListAvatarContainer>
                  );
                },
              },
              {
                name: "typeOfMember",
                title: translate("typeOfMember"),
              },
              {
                name: "email",
                title: translate("email"),
              },
            ]}
            rowActionsExtra={[
              {
                title: "remove",
                buttonAndAction: (memberId) => (
                  <IconButton onClick={() => onRemoveMemberSelected(memberId)}>
                    <IconCloseCircle width={20} height={20} color={colors.gray600} />
                  </IconButton>
                ),
              },
            ]}
            emptyData={translate("noMembersToShow")}
            pageSizeList={[20, 40, 60, 100]}
            currentPage={0}
            positionActionsColumn="right"
            showColumnChooser={false}
            showActionLabel={false}
            showFooterGrid={false}
            showFooterPageInfo={false}
            showHeaderGrid={false}
            showSelection={false}
            showSort={false}
          />
        </MemberListContainer>
      );
    }

    return null;
  };

  if (!membersAdded?.length) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" padding="32px 0px">
        <Box className={classes.accUserLogo}>
          <img src={noUserLogo} alt="noUserLogo" className={classes.accUserLogoImg} />
        </Box>
        <Typography className={classes.noMemberHeading}>
          {translate("noMembersAssociatedToThisEquipment")}
        </Typography>
        <Typography className={classes.noMemberSubheading}>
          {translate("youCanStartByAddingAMember")}
        </Typography>
        <SplitButton
          customColor={Color.SECONDARY_DARK}
          onClick={() => setIsAddMemberModalOpened(true)}
        >
          {translate("addMember")}
        </SplitButton>

        <AddMemberModal
          isOpened={isAddMemberModalOpened}
          onClose={() => setIsAddMemberModalOpened(false)}
          membersIdAdded={membersAdded.map((member) => member.id as unknown as string)}
          onConfirmAddMembers={onAddMembersSelected}
        />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center" padding="32px 0px">
      <Grid container direction="column" item xs={12}>
        <MemberHeaderContainer item xs={12} container direction="row">
          <MemberSearchFieldContainer item xs={4}>
            <Input
              placeholder={translate("typeInToSearchAMember")}
              rightIcon={<SearchIcon />}
              value={searchMemberValue}
              onChange={(event) => setSearchMemberValue(event.target.value)}
            />
          </MemberSearchFieldContainer>
          <Grid item xs={6}>
            <ButtonGroup
              activeKey={memberListActive}
              buttons={[
                {
                  key: ListOption.GALLERY,
                  label: translate("gallery"),
                  onClick: () => setMemberListActive(ListOption.GALLERY),
                },
                {
                  key: ListOption.LIST,
                  label: translate("list"),
                  onClick: () => setMemberListActive(ListOption.LIST),
                },
              ]}
            />
          </Grid>
          <AddMemberButtonContainer item xs={2}>
            <SplitButton
              customColor={Color.SECONDARY_DARK}
              onClick={() => setIsAddMemberModalOpened(true)}
            >
              {translate("addMember")}
            </SplitButton>
          </AddMemberButtonContainer>
        </MemberHeaderContainer>
        {renderMemberContentByOption()}
      </Grid>

      <AddMemberModal
        isOpened={isAddMemberModalOpened}
        onClose={() => setIsAddMemberModalOpened(false)}
        membersIdAdded={membersAdded.map((member) => member.id as unknown as string)}
        onConfirmAddMembers={onAddMembersSelected}
      />
    </Box>
  );
};
